/*
  PaoPao wishlist — site-wide.

  Stores a list of saved products client-side and keeps every heart button and
  the header count badge in sync. Loaded once from layout/theme.liquid.

  Heart buttons opt in by carrying `data-wishlist-toggle` plus the product data
  attributes (see readItem). Clicks are handled by delegation, so cards injected
  after load — recently-viewed, predictive search, the wishlist page itself —
  work with no extra wiring. A debounced MutationObserver re-applies the active
  (filled-heart) state to any cards that appear later.

  Storage seam: the Store object below is the ONLY place that touches local
  persistence (localStorage under `paopao:wishlist`). Account sync layers on top:
  when a customer is signed in on the storefront (via WishlistAccount, the
  Customer Account API client in wishlist-account.js), the local list is the fast
  cache and the customer metafield `paopao.wishlist` is the source of truth. On
  load we hydrate from the account, union it with the guest list (merge-on-login),
  and write the result back; every change writes through to the account (debounced).
  Guests with no storefront session keep working purely on localStorage. Items are
  stored in the same shape as `paopao:recentlyViewed` so a saved entry is
  self-describing.
*/
(function () {
  'use strict';

  var KEY = 'paopao:wishlist';
  var MAX = 100;
  var CHANGE_EVENT = 'paopao:wishlist:change';

  /* ---------------------------------------------------------------- storage */
  var Store = {
    getAll: function () {
      try { return JSON.parse(localStorage.getItem(KEY) || '[]') || []; }
      catch (e) { return []; }
    },
    setAll: function (list) {
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); }
      catch (e) {}
    }
  };

  /* ------------------------------------------------------------------- core */
  function has(handle) {
    if (!handle) return false;
    return Store.getAll().some(function (x) { return x && x.handle === handle; });
  }

  function count() { return Store.getAll().length; }

  function add(item) {
    if (!item || !item.handle) return;
    var list = Store.getAll().filter(function (x) { return x && x.handle !== item.handle; });
    list.unshift(item);
    commit(list);
  }

  function remove(handle) {
    if (!handle) return;
    commit(Store.getAll().filter(function (x) { return x && x.handle !== handle; }));
  }

  /* --------------------------------------------------------- account sync */
  // Writes always hit the local cache; when signed in they also write through
  // to the customer metafield (debounced). See wishlist-account.js.
  var CA = window.WishlistAccount || null;
  var acctId = null;        // customer GID, learned on hydrate
  var putTimer = null;
  var putPending = false;

  function accountEnabled() { return !!(CA && CA.isConfigured && CA.isConfigured()); }

  function commit(list) {
    Store.setAll(list);
    scheduleRemotePut();
  }

  function scheduleRemotePut() {
    if (!accountEnabled() || !acctId) return;
    putPending = true;
    clearTimeout(putTimer);
    putTimer = setTimeout(function () { flushRemote(false); }, 500);
  }

  function flushRemote(keepalive) {
    if (!accountEnabled() || !acctId || !putPending) return;
    putPending = false;
    try {
      CA.saveFavorites(acctId, Store.getAll(), keepalive ? { keepalive: true } : undefined)
        .catch(function () {});
    } catch (e) {}
  }

  function mergeByHandle(a, b) {
    var seen = {}, out = [];
    a.concat(b).forEach(function (x) {
      if (x && x.handle && !seen[x.handle]) { seen[x.handle] = true; out.push(x); }
    });
    return out.slice(0, MAX);
  }

  // Clears the local cache when a buyer who WAS linked no longer has a session
  // (refresh token revoked → they signed out). The account keeps the data and
  // re-merges on next sign-in, so this is non-destructive. A transient failure
  // keeps the refresh token (hasStoredSession stays true) so we don't clear.
  function clearLocalIfSignedOut(hadSession) {
    if (hadSession && !(CA.hasStoredSession && CA.hasStoredSession())) {
      Store.setAll([]);
      acctId = null;
      syncAll();
      emitChange();
      return true;
    }
    return false;
  }

  // On load: hydrate + merge guest list into the account when signed in; detect
  // sign-out and clear locally; silently bootstrap a confirmed-logged-in buyer.
  function hydrateFromAccount() {
    if (!accountEnabled()) return;
    var hadSession = !!(CA.hasStoredSession && CA.hasStoredSession());
    CA.ensureToken().then(function (tok) {
      if (!tok) {
        if (clearLocalIfSignedOut(hadSession)) return;   // signed out → cleared
        // Not linked: auto-bootstrap (a one-time, expected prompt=none bounce)
        // only when we're confident the buyer is authenticated. Guests are never
        // bounced — favoriting nudges them via the sign-in toast instead.
        if (!hadSession && (loggedInHint() || cameFromLogin()) && CA.autoBootstrap) {
          CA.autoBootstrap();
        }
        return;
      }
      return CA.getFavorites().then(function (res) {
        acctId = res && res.id;
        if (!acctId) return;
        var account = (res && res.items) || [];
        // The account is the source of truth → MIRROR it, so un-favorites made on
        // another device propagate here. The ONE exception is the guest→login
        // moment: fold the guest's local favorites up into the account, once per
        // login (tracked by comparing the login token to what we last folded).
        var linkToken = null, lastFolded = null;
        try { linkToken = localStorage.getItem('paopao:ca:justlinked'); } catch (e) {}
        try { lastFolded = localStorage.getItem('paopao:wl:folded'); } catch (e) {}
        if (linkToken && linkToken !== lastFolded) {
          try { localStorage.setItem('paopao:wl:folded', linkToken); } catch (e) {}
          var merged = mergeByHandle(Store.getAll(), account);
          Store.setAll(merged);
          syncAll();
          emitChange();
          if (merged.length !== account.length) { putPending = true; flushRemote(false); }
        } else {
          Store.setAll(account.slice(0, MAX));
          syncAll();
          emitChange();
        }
      });
    }).catch(function () { clearLocalIfSignedOut(hadSession); });
  }

  // Best-effort flush of a pending write when the page goes away.
  window.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushRemote(true);
  });
  window.addEventListener('pagehide', function () { flushRemote(true); });

  /* ---------------------------------------------------- sign-in detection */
  function loggedInHint() {
    return !!(window.PaoPaoCustomer && window.PaoPaoCustomer.loggedIn);
  }

  // Heuristic: did this pageview arrive from Shopify's login/account flow? Lets
  // us run the (expected) auto-bootstrap right after a customer signs in, since
  // the storefront `customer` object is unreliable on new customer accounts.
  function cameFromLogin() {
    try {
      if (!document.referrer) return false;
      var h = new URL(document.referrer).hostname;
      return h === 'shopify.com' || /\.shopify\.com$/.test(h) || /^account\./.test(h);
    } catch (e) { return false; }
  }

  /* -------------------------------------------------------- sign-in toast */
  // A guest (or logged-in-but-unsynced buyer) who favorites is nudged to sign in
  // so their favorites persist to their account. The sign-in bounce only happens
  // when they click the toast's action — never on the heart click itself.
  var TOAST_SHOWN = 'paopao:wl:toastShown';    // sessionStorage prefix (per context): shown this session
  var TOAST_SNOOZE = 'paopao:wl:toastSnooze';  // localStorage prefix (per context): dismissed-until timestamp
  var TOAST_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
  var toastEl = null, toastTimer = null;
  var currentToastCtx = 'wishlist';            // which trigger owns the currently-shown toast

  function initToast() {
    toastEl = document.querySelector('[data-wl-toast]');
    if (!toastEl) return;
    var action = toastEl.querySelector('[data-wl-toast-action]');
    var closeBtn = toastEl.querySelector('[data-wl-toast-close]');
    if (action) {
      action.addEventListener('click', function () {
        hideToast();
        var acc = window.PaoPaoAccount || CA;
        if (acc && acc.login) acc.login();          // → /account (login), then auto-sync on return
        else if (acc && acc.signIn) acc.signIn();   // fallback
      });
    }
    if (closeBtn) closeBtn.addEventListener('click', dismissToast);
  }

  function shownKey(ctx) { return TOAST_SHOWN + ':' + ctx; }
  function snoozeKey(ctx) { return TOAST_SNOOZE + ':' + ctx; }

  function toastSnoozed(ctx) {
    try {
      var until = parseInt(localStorage.getItem(snoozeKey(ctx)) || '0', 10) || 0;
      return Date.now() < until;
    } catch (e) { return false; }
  }

  // Sign-in nudge. `copy` (optional) overrides the toast text; `ctx` names the
  // trigger ('wishlist' | 'custom-pieces') so the once-per-session / snooze
  // guards are tracked PER context — favoriting and saving a design can each
  // prompt once, independently. The already-linked/authed guards are global.
  function maybePromptSignin(copy, ctx) {
    ctx = ctx || 'wishlist';
    var acc = window.PaoPaoAccount || CA;
    if (!acc || !toastEl) return;
    if (acc.isConfigured && !acc.isConfigured()) return;
    if (acc.isAuthed && acc.isAuthed()) return;                      // already synced
    if (acc.hasStoredSession && acc.hasStoredSession()) return;      // linked already (token just lapsed)
    try { if (sessionStorage.getItem(shownKey(ctx))) return; } catch (e) {}
    if (toastSnoozed(ctx)) return;
    currentToastCtx = ctx;
    showToast(copy);
  }

  function setToastCopy(copy) {
    if (!copy) return;
    var t = toastEl.querySelector('[data-wl-toast-title]');
    var s = toastEl.querySelector('[data-wl-toast-sub]');
    var a = toastEl.querySelector('[data-wl-toast-action]');
    if (copy.title && t) t.textContent = copy.title;
    if (copy.text && s) s.textContent = copy.text;
    if (copy.button && a) a.textContent = copy.button;
  }

  function showToast(copy) {
    if (!toastEl) return;
    setToastCopy(copy);
    try { sessionStorage.setItem(shownKey(currentToastCtx), '1'); } catch (e) {}
    toastEl.classList.add('is-open');
    toastEl.setAttribute('aria-hidden', 'false');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 5500);
  }

  function hideToast() {
    if (!toastEl) return;
    clearTimeout(toastTimer);
    toastEl.classList.remove('is-open');
    toastEl.setAttribute('aria-hidden', 'true');
  }

  function dismissToast() {
    try { localStorage.setItem(snoozeKey(currentToastCtx), String(Date.now() + TOAST_SNOOZE_MS)); } catch (e) {}
    hideToast();
  }

  // Returns the resulting active state (true = now saved).
  function toggle(item) {
    if (!item || !item.handle) return false;
    if (has(item.handle)) { remove(item.handle); return false; }
    add(item); return true;
  }

  /* --------------------------------------------------------------------- ui */
  function readItem(btn) {
    var d = btn.dataset;
    return {
      handle: d.wishlistHandle,
      title: d.wishlistTitle,
      type: d.wishlistType,
      url: d.wishlistUrl,
      price: d.wishlistPrice,
      image: d.wishlistImage,
      variantId: d.wishlistVariant,
      available: d.wishlistAvailable === 'true'
    };
  }

  function paintButton(btn, active) {
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.classList.toggle('is-wishlisted', !!active);
  }

  function syncButtons(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      paintButton(btn, has(btn.getAttribute('data-wishlist-handle')));
    });
  }

  // Mirrors the cart count badge: text = count, hidden when empty.
  function syncCount() {
    var c = count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = c;
      if (el.hasAttribute('data-wishlist-count-hideable')) {
        el.classList.toggle('hidden!', c === 0);
      }
    });
  }

  function syncAll(root) { syncButtons(root); syncCount(); }

  function emitChange() {
    try {
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { count: count() } }));
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- wiring */
  // Delegated so injected cards work without re-binding.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    var nowActive = toggle(readItem(btn));
    // Repaint every button for this product (a card and the PDP can coexist).
    syncButtons();
    syncCount();
    emitChange();
    // Make sure the clicked one reflects state even if it carries no handle match.
    paintButton(btn, nowActive);
    // Nudge unsynced buyers to sign in so favorites persist to their account.
    if (nowActive) maybePromptSignin();
  });

  // Cross-tab: another tab changed the list.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { syncAll(); emitChange(); }
  });

  // Re-apply state to cards added after load (recently-viewed, search, etc.).
  var rafPending = false;
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      if (rafPending) return;
      var relevant = mutations.some(function (m) {
        return Array.prototype.some.call(m.addedNodes, function (n) {
          return n.nodeType === 1 &&
            (n.matches && n.matches('[data-wishlist-toggle]') ||
             n.querySelector && n.querySelector('[data-wishlist-toggle]'));
        });
      });
      if (!relevant) return;
      rafPending = true;
      requestAnimationFrame(function () { rafPending = false; syncButtons(); });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() { syncAll(); initToast(); hydrateFromAccount(); }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  /* ------------------------------------------------------------------- api */
  window.PaoPaoWishlist = {
    getAll: Store.getAll,
    has: has,
    add: function (item) { add(item); syncAll(); emitChange(); },
    remove: function (handle) { remove(handle); syncAll(); emitChange(); },
    toggle: function (item) { var a = toggle(item); syncAll(); emitChange(); return a; },
    count: count,
    refresh: syncAll
  };

  // Shared sign-in nudge for other features (e.g. saved custom pieces in the
  // configurator). Pass copy { title, text, button } to override the wording.
  window.PaoPaoSigninToast = {
    prompt: function (copy, ctx) { maybePromptSignin(copy, ctx); },
    hide: hideToast
  };
})();
